// src/components/programs/JobOrders/PrintJobOrder.jsx
import React from "react";

const PrintJobOrder = ({ program, jobOrder, lng, t, logoUrl }) => {
    if (!jobOrder) return null;

    const isArabic = lng === "ar";
    const paxBreakdown = jobOrder.pax_breakdown || [];

    const getPaxQty = (keyword) => {
        const type = paxBreakdown.find(p => p.name?.en?.toLowerCase().includes(keyword) || p.name?.ar?.includes(keyword));
        return type ? type.quantity : 0;
    };

    const adults = getPaxQty('adult') || getPaxQty('بالغ');
    const children = getPaxQty('child') || getPaxQty('أطفال');
    const infants = getPaxQty('infant') || getPaxQty('رضيع');

    const thStyle = {
        backgroundColor: '#d9edf7',
        color: '#000',
        fontWeight: 'bold',
        padding: '10px 8px',
        border: '1px solid #000',
        fontSize: '13px',
        whiteSpace: 'nowrap'
    };

    const tdStyle = {
        backgroundColor: '#fff',
        color: '#000',
        padding: '10px 8px',
        border: '1px solid #000',
        fontSize: '13px'
    };

    const opDetails = jobOrder.operational_details || {};
    const pTypeStr = (opDetails.product_type || "").toLowerCase();

    const renderDynamicRows = () => {
        const sName = jobOrder.service_name?.[lng] || jobOrder.service_name?.en || "";

        if (pTypeStr === "hotel") {
            const hotel = opDetails.hotel || {};
            const nights = opDetails.nights || hotel.nights || "-";

            let checkOut = opDetails.checkout_date || hotel.checkout_date;
            if (!checkOut && hotel.room_stays && hotel.room_stays.length > 0) {
                const sortedStays = [...hotel.room_stays].sort((a, b) => new Date(a.date) - new Date(b.date));
                const lastStayDate = sortedStays[sortedStays.length - 1].date;
                const dateObj = new Date(lastStayDate + 'T12:00:00Z');
                dateObj.setDate(dateObj.getDate() + 1);
                checkOut = dateObj.toISOString().split('T')[0];
            }
            checkOut = checkOut || "-";

            let roomType = hotel.room_category?.name?.[lng] || hotel.room_category?.name?.en || opDetails.room_type || hotel.room_type || "-";
            if (typeof roomType === 'object' && roomType !== null) {
                roomType = roomType.name?.[lng] || roomType.name?.en || "-";
            }

            let mealPlan = hotel.meal_plan?.name?.[lng] || hotel.meal_plan?.name?.en || opDetails.meal_plan || hotel.meal_plan || "-";
            if (typeof mealPlan === 'object' && mealPlan !== null) {
                mealPlan = mealPlan.name?.[lng] || mealPlan.name?.en || "-";
            }

            return (
                <>
                    <tr>
                        <td style={thStyle}>{isArabic ? "اسم الفندق" : "HOTEL NAME"}</td>
                        <td style={tdStyle} colSpan={2}>{sName}</td>
                        <td style={thStyle}>{isArabic ? "عدد الليالي" : "NIGHTS"}</td>
                        <td style={tdStyle} colSpan={2}>{nights}</td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "تاريخ الدخول" : "CHECK IN"}</td>
                        <td style={tdStyle} colSpan={2}>{jobOrder.activity_date || "-"}</td>
                        <td style={thStyle}>{isArabic ? "تاريخ الخروج" : "CHECK OUT"}</td>
                        <td style={tdStyle} colSpan={2}>{checkOut}</td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "نوع الغرفة" : "ROOM TYPE"}</td>
                        <td style={tdStyle} colSpan={2}>{roomType}</td>
                        <td style={thStyle}>{isArabic ? "الوجبات" : "MEAL PLAN"}</td>
                        <td style={tdStyle} colSpan={2}>{mealPlan}</td>
                    </tr>
                </>
            );
        } else if (pTypeStr === "flight") {
            const flight = opDetails.flight || {};
            const depCity = flight.departure_airport?.city?.name?.[lng] || flight.departure_airport?.city?.name?.en || flight.departure_airport?.name?.[lng] || "-";
            const arrCity = flight.arrival_airport?.city?.name?.[lng] || flight.arrival_airport?.city?.name?.en || flight.arrival_airport?.name?.[lng] || "-";
            const airline = flight.airline?.name?.[lng] || flight.airline?.name?.en || "-";
            const pnr = flight.pnr || "-";
            const depTime = flight.departure_time || "-";
            const arrTime = flight.arrival_time || "-";
            const baggage = flight.baggage_weight ? `${flight.baggage_weight} ${flight.baggage_unit || ''}` : "-";

            return (
                <>
                    <tr>
                        <td style={thStyle}>{isArabic ? "رقم الرحلة" : "FLIGHT NO."}</td>
                        <td style={tdStyle} colSpan={2}>{flight.flight_number || "-"}</td>
                        <td style={thStyle}>{isArabic ? "تاريخ الرحلة" : "FLIGHT DATE"}</td>
                        <td style={tdStyle} colSpan={2}>{jobOrder.activity_date || flight.departure_date || "-"}</td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "الاقلاع من" : "DEPARTURE"}</td>
                        <td style={tdStyle} colSpan={2}>{depCity} ({depTime})</td>
                        <td style={thStyle}>{isArabic ? "الوصول الى" : "ARRIVAL"}</td>
                        <td style={tdStyle} colSpan={2}>{arrCity} ({arrTime})</td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "الخطوط الجوية" : "AIRLINE"}</td>
                        <td style={tdStyle} colSpan={2}>{airline}</td>
                        <td style={thStyle}>{isArabic ? "رقم الحجز (PNR)" : "PNR"}</td>
                        <td style={tdStyle} colSpan={2}>{pnr}</td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "الوزن المسموح" : "BAGGAGE"}</td>
                        <td style={tdStyle} colSpan={5}>{baggage}</td>
                    </tr>
                </>
            );
        } else if (pTypeStr === "transfer") {
            const transfer = opDetails.transfer || {};
            const time = opDetails.pickup_time || opDetails.time || transfer.pickup_time || transfer.time || "-";
            const jobPickup = jobOrder.pickup_location?.[lng] || (typeof jobOrder.pickup_location === 'string' ? jobOrder.pickup_location : "") || jobOrder.pickup_location?.ar || jobOrder.pickup_location?.en;
            const jobDropoff = jobOrder.drop_off_location?.[lng] || (typeof jobOrder.drop_off_location === 'string' ? jobOrder.drop_off_location : "") || jobOrder.drop_off_location?.ar || jobOrder.drop_off_location?.en;
            const pickup = jobPickup || opDetails.pickup_location || transfer.pickup_location || "-";
            const dropoff = jobDropoff || opDetails.dropoff_location || transfer.dropoff_location || "-";

            return (
                <>
                    <tr>
                        <td style={thStyle}>{isArabic ? "الخدمة" : "SERVICE NAME"}</td>
                        <td style={tdStyle} colSpan={2}>{sName}</td>
                        <td style={thStyle}>{isArabic ? "الوقت" : "TIME"}</td>
                        <td style={tdStyle} colSpan={2}>{time}</td>
                    </tr>
                    {(pickup !== "-" || dropoff !== "-") && (
                        <tr>
                            <td style={thStyle}>{isArabic ? "مكان الاستقبال" : "PICK UP"}</td>
                            <td style={tdStyle} colSpan={2}>{pickup}</td>
                            <td style={thStyle}>{isArabic ? "مكان التوصيل" : "DROP OFF"}</td>
                            <td style={tdStyle} colSpan={2}>{dropoff}</td>
                        </tr>
                    )}
                </>
            );
        } else {
            // Tour / Default
            const tour = opDetails.tour || opDetails.activity || {};
            const time = opDetails.pickup_time || opDetails.time || tour.pickup_time || tour.time || "-";
            const jobPickup = jobOrder.pickup_location?.[lng] || (typeof jobOrder.pickup_location === 'string' ? jobOrder.pickup_location : "") || jobOrder.pickup_location?.ar || jobOrder.pickup_location?.en;
            const jobDropoff = jobOrder.drop_off_location?.[lng] || (typeof jobOrder.drop_off_location === 'string' ? jobOrder.drop_off_location : "") || jobOrder.drop_off_location?.ar || jobOrder.drop_off_location?.en;
            const pickup = jobPickup || opDetails.pickup_location || tour.pickup_location || "-";
            const dropoff = jobDropoff || opDetails.dropoff_location || tour.dropoff_location || "-";

            return (
                <>
                    <tr>
                        <td style={thStyle}>{isArabic ? "الخدمة" : "TOUR PROGRAM"}</td>
                        <td style={tdStyle} colSpan={2}>{sName}</td>
                        <td style={thStyle}>{isArabic ? "وقت الاستقبال" : "PICK UP TIME"}</td>
                        <td style={tdStyle} colSpan={2}>{time}</td>
                    </tr>
                    {(pickup !== "-" || dropoff !== "-") && (
                        <tr>
                            <td style={thStyle}>{isArabic ? "مكان الاستقبال" : "PICK UP"}</td>
                            <td style={tdStyle} colSpan={2}>{pickup}</td>
                            <td style={thStyle}>{isArabic ? "مكان التوصيل" : "DROP OFF"}</td>
                            <td style={tdStyle} colSpan={2}>{dropoff}</td>
                        </tr>
                    )}
                </>
            );
        }
    };

    return (
        <div style={{
            fontFamily: isArabic ? "'Cairo', sans-serif" : "'Inter', sans-serif",
            direction: isArabic ? "rtl" : "ltr",
            padding: "40px",
            color: "#333",
            backgroundColor: "#fff",
            lineHeight: 1.6
        }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                {logoUrl && <img src={logoUrl} alt="Logo" style={{ maxHeight: "100px", marginBottom: "10px" }} />}
                <h1 style={{ margin: 0, fontSize: "24px", color: "blue", textTransform: "uppercase" }}>
                    {isArabic ? "أمر شغل" : "JOB ORDER"}
                </h1>
            </div>

            {/* Main Table */}
            <table style={{ width: "100%", borderCollapse: "collapse", border: "2px solid #000" }}>
                <tbody>
                    <tr>
                        <td style={thStyle}>{isArabic ? "رقم الجوب" : "JOB ID"}</td>
                        <td style={tdStyle} colSpan={2}>{jobOrder.job_number || "-"}</td>
                        <td style={thStyle}>{isArabic ? "رقم الفاوتشر" : "VOUCHER NO."}</td>
                        <td style={tdStyle} colSpan={2}>{jobOrder.voucher_number || "-"}</td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "تاريخ الخدمة" : (pTypeStr === 'hotel' ? "CHECK IN DATE" : pTypeStr === 'flight' ? "FLIGHT DATE" : "TOUR DATE")}</td>
                        <td style={tdStyle} colSpan={2}>{jobOrder.activity_date || "-"}</td>
                        <td style={thStyle}>{isArabic ? "اسم المورد" : "SUPPLIER'S NAME"}</td>
                        <td style={tdStyle} colSpan={2}>{jobOrder.supplier?.name?.[lng] || jobOrder.supplier?.name?.en || "-"}</td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "اسم العميل" : "CUSTOMER NAME"}</td>
                        <td style={tdStyle} colSpan={5}>
                            {typeof jobOrder.customer_name === 'string' ? jobOrder.customer_name : (jobOrder.customer_name?.[lng] || jobOrder.customer_name?.en || "-")}
                        </td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "بالغين" : "ADULTS"}</td>
                        <td style={tdStyle} colSpan={1}>{adults}</td>
                        <td style={thStyle}>{isArabic ? "أطفال" : "CHILDRENS"}</td>
                        <td style={tdStyle} colSpan={1}>{children}</td>
                        <td style={thStyle}>{isArabic ? "رُضع" : "INFINTS"}</td>
                        <td style={tdStyle} colSpan={1}>{infants}</td>
                    </tr>

                    {renderDynamicRows()}

                    {/* Additional fields if provided: Cash On Tour */}
                    {jobOrder.cash_on_tour !== undefined && jobOrder.cash_on_tour !== null && jobOrder.cash_on_tour !== "" && (
                        <tr>
                            <td style={thStyle}>{isArabic ? "مبلغ التحصيل (نقداً)" : "CASH ON TOUR"}</td>
                            <td style={tdStyle} colSpan={5}>
                                <strong style={{ color: "#d32f2f", fontSize: "14px" }}>
                                    {String(jobOrder.cash_on_tour)}
                                </strong>
                            </td>
                        </tr>
                    )}

                    <tr>
                        <td style={thStyle}>{isArabic ? "حجز بواسطة" : "BOOKING BY"}</td>
                        <td style={tdStyle} colSpan={2}>{jobOrder.booked_by?.name || "-"}</td>
                        <td style={thStyle}>{isArabic ? "تأكيد بواسطة" : "CONFIRM BY"}</td>
                        <td style={tdStyle} colSpan={2}>{jobOrder.confirmed_by?.name || jobOrder.print_candidate?.name || "-"}</td>
                    </tr>

                    {/* Remarks Section: Displays ONLY Job Order Notes and Job Order Remarks */}
                    <tr>
                        <td style={thStyle}>{isArabic ? "ملاحظات" : "REMARKS"}</td>
                        <td style={{ ...tdStyle, padding: "12px 14px", verticalAlign: "top" }} colSpan={5}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                {/* 1. Job Order Notes */}
                                {(jobOrder.notes?.[lng] || (typeof jobOrder.notes === 'string' && jobOrder.notes) || jobOrder.notes?.ar || jobOrder.notes?.en) && (
                                    <div style={{
                                        fontWeight: "600",
                                        color: "#0288d1",
                                        fontSize: "13px",
                                        lineHeight: 1.5,
                                        whiteSpace: "pre-line",
                                        paddingBottom: jobOrder.remarks ? "6px" : 0,
                                        borderBottom: jobOrder.remarks ? "1px dashed #e0e0e0" : "none"
                                    }}>
                                        {jobOrder.notes?.[lng] || (typeof jobOrder.notes === 'string' ? jobOrder.notes : "") || jobOrder.notes?.ar || jobOrder.notes?.en}
                                    </div>
                                )}
                                
                                {/* 2. Job Order Remarks */}
                                {jobOrder.remarks && (
                                    <div style={{
                                        fontSize: "12.5px",
                                        color: "#d32f2f",
                                        fontWeight: "500",
                                        backgroundColor: "#ffebee",
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        display: "inline-block"
                                    }}>
                                        {typeof jobOrder.remarks === 'object' ? (jobOrder.remarks?.[lng] || jobOrder.remarks?.ar || jobOrder.remarks?.en) : jobOrder.remarks}
                                    </div>
                                )}

                                {/* Fallback if nothing exists */}
                                {!jobOrder.notes && !jobOrder.remarks && (
                                    <span style={{ color: "#888" }}>-</span>
                                )}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Payment Notes if provided */}
            <div style={{ marginTop: "15px", fontSize: "14px" }}>
                {jobOrder.payment_notes && (
                    <div style={{ color: "blue" }}>
                        <strong>{isArabic ? "ملاحظات الدفع" : "Payment Notes"}:</strong> <span style={{ color: "#333" }}>{typeof jobOrder.payment_notes === 'object' ? (jobOrder.payment_notes?.[lng] || jobOrder.payment_notes?.ar || jobOrder.payment_notes?.en) : jobOrder.payment_notes}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PrintJobOrder;
